import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import MasterclassModal from "@/components/MasterclassModal";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
          scrolled
            ? "bg-tnn-warm/95 backdrop-blur-md border-tnn-ink/8 shadow-[0_4px_24px_rgba(40,0,40,0.05)]"
            : "bg-tnn-warm/80 backdrop-blur-sm border-transparent"
        }`}
      >
        <div
          className={`max-w-6xl mx-auto px-6 flex items-center justify-between transition-[height] duration-300 ${
            scrolled ? "h-16" : "h-[76px]"
          }`}
        >
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img
              src="/assets/logo.png"
              alt="The Nutrition Network Africa"
              className={`w-auto transition-[height] duration-300 ${scrolled ? "h-10" : "h-[52px]"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-[13px] xl:text-sm whitespace-nowrap font-medium transition-colors duration-200 ${
                    isActive ? "text-tnn-purple" : "text-tnn-muted hover:text-tnn-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="header-cta-partner"
              className="ml-2 inline-flex items-center gap-1.5 whitespace-nowrap bg-tnn-purple text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 hover:bg-tnn-deep"
            >
              Partner With Us
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-tnn-ink"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-tnn-warm flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[76px] border-b border-tnn-ink/8">
              <img src="/assets/logo.png" alt="The Nutrition Network Africa" className="h-10 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2 text-tnn-ink" aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 + i * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block font-display text-2xl font-semibold text-tnn-ink py-3 hover:text-tnn-purple transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="pt-6"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-cta-partner"
                  className="inline-flex items-center gap-2 bg-tnn-purple text-white font-semibold text-sm px-6 py-3.5 rounded-full"
                >
                  Partner With Us <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      <MasterclassModal />
    </>
  );
}
